import { useState, useEffect } from 'react';
import { Save, Play, Trash2, Loader2, TrendingUp, TrendingDown, DollarSign, Package, Maximize2, Copy, Download, History, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { planningApi } from '../../api/planning';
import ExpandedPartsTable from './ExpandedPartsTable';
import * as XLSX from 'xlsx';
import { devLog } from '../../utils/formatters';

export default function ScenarioWorkspace({ scenario, onUpdate, onDelete }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [details, setDetails] = useState(null);
  const [changes, setChanges] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExpandedTable, setShowExpandedTable] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    fetchDetails();
  }, [scenario.id]);

  const fetchDetails = async () => {
    setLoading(true);
    const result = await planningApi.getScenarioDetail(scenario.id);
    if (result.success) {
      setDetails(result.data);
      // Initialize changes from existing scenario
      const existingChanges = result.data.scenario.changes || {};
      setChanges(existingChanges.part_changes || {});
    }
    setLoading(false);
  };


  const fetchVersions = async () => {
    const result = await planningApi.getVersionHistory(scenario.id);
    if (result.success) {
      setVersions(result.data);
    }
  };


  const handlePartChange = (partId, field, value) => {
    devLog(`Parent handlePartChange: Part ${partId}, Field ${field}, Value ${value}`);
    
    setChanges(prev => {
      const partChanges = prev[partId] || {};
      
      if (field === 'piece_price_cents') {
        partChanges.piece_price_cents = parseInt(value) || 0;
      } else if (field.startsWith('volume_')) {
        const year = field.replace('volume_', '');
        if (!partChanges.volumes) partChanges.volumes = {};
        partChanges.volumes[year] = parseInt(value) || 0;
      }
      
      const newChanges = {
        ...prev,
        [partId]: partChanges
      };
      
      return newChanges;
    });
    
    setHasUnsavedChanges(true);
  };

  const handleSave = async (passedChanges = null) => {
    const changesToSave = passedChanges || changes;
    devLog(`Parent handleSave called with changes:`, changesToSave);
    setSaving(true);
    
    const result = await planningApi.updateScenario(scenario.id, {
      changes: {
        part_changes: changesToSave
      }
    });
    
    devLog(`Backend API result:`, result);
    
    if (result.success) {
      // Update the main changes state with the passed changes
      if (passedChanges) {
        setChanges(passedChanges);
      }
      onUpdate(result.data);
      setHasUnsavedChanges(false);
      // Refresh details to get updated calculations
      fetchDetails();
    } else {
      console.error(`Save failed:`, result.error);
    }
    
    setSaving(false);
  };

  const handleActivate = async () => {
    if (!confirm('Set this scenario as the active planning basis for the opportunity? This updates planning rollups without overwriting part master forecasts.')) {
      return;
    }
    
    setActivating(true);
    const result = await planningApi.activateScenario(scenario.id);
    if (result.success) {
      alert(result.data.message);
      onUpdate({ ...scenario, status: 'active' });
      fetchDetails();
    }
    setActivating(false);
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to archive this scenario?')) {
      return;
    }
    
    const result = await planningApi.deleteScenario(scenario.id);
    if (result.success) {
      onDelete(scenario.id);
    }
  };

  const handleDuplicate = async () => {
    const result = await planningApi.duplicateScenario(scenario.id);
    if (result.success) {
      // Notify parent to refresh the list and select the new scenario
      onUpdate(result.data);
      alert(`Scenario duplicated successfully as "${result.data.name}"`);
    } else {
      alert(`Failed to duplicate scenario: ${result.error}`);
    }
  };

  const handleExportToExcel = () => {
    if (!details) return;
    
    const { scenario: scenarioData, parts_comparison } = details;
    
    // Prepare data for Excel
    const excelData = [];
    
    // Add header section
    excelData.push(['Scenario Export']);
    excelData.push(['Scenario Name:', scenarioData.name]);
    excelData.push(['Opportunity:', scenarioData.opportunity_name]);
    excelData.push(['Status:', scenarioData.status]);
    excelData.push(['Created:', new Date(scenarioData.created_at).toLocaleDateString()]);
    excelData.push([]);
    
    // Add summary section
    excelData.push(['Summary']);
    excelData.push(['Baseline Revenue:', `$${(scenarioData.revenue_baseline_cents / 100).toLocaleString()}`]);
    excelData.push(['Scenario Revenue:', `$${(scenarioData.revenue_scenario_cents / 100).toLocaleString()}`]);
    excelData.push(['Revenue Impact:', `$${(scenarioData.revenue_delta_cents / 100).toLocaleString()}`]);
    excelData.push(['Impact %:', `${scenarioData.revenue_delta_percent.toFixed(2)}%`]);
    excelData.push([]);
    
    // Collect all years
    const allYears = new Set();
    parts_comparison.forEach(part => {
      Object.keys(part.baseline_volumes || {}).forEach(year => allYears.add(year));
      Object.keys(part.scenario_volumes || {}).forEach(year => allYears.add(year));
    });
    const years = Array.from(allYears).sort();
    
    // Add parts data header
    const partsHeader = [
      'Part Number',
      'Description',
      'Baseline Price',
      'Scenario Price',
      'Price Change',
      'Baseline Total Volume',
      'Scenario Total Volume',
      'Volume Change',
      'Baseline Revenue',
      'Scenario Revenue',
      'Revenue Impact'
    ];
    
    // Add year columns
    years.forEach(year => {
      partsHeader.push(`${year} Baseline Vol`);
      partsHeader.push(`${year} Scenario Vol`);
    });
    
    excelData.push(['Parts Detail']);
    excelData.push(partsHeader);
    
    // Add parts data
    parts_comparison.forEach(part => {
      const baselineVolTotal = Object.values(part.baseline_volumes || {}).reduce((sum, vol) => sum + vol, 0);
      const scenarioVolTotal = Object.values(part.scenario_volumes || {}).reduce((sum, vol) => sum + vol, 0);
      const volChange = scenarioVolTotal - baselineVolTotal;
      const priceChange = part.scenario_piece_price_cents - part.baseline_piece_price_cents;
      const revenueImpact = part.scenario_revenue_cents - part.baseline_revenue_cents;
      
      const row = [
        part.part_number,
        part.description,
        part.baseline_piece_price_cents / 100,
        part.scenario_piece_price_cents / 100,
        priceChange / 100,
        baselineVolTotal,
        scenarioVolTotal,
        volChange,
        part.baseline_revenue_cents / 100,
        part.scenario_revenue_cents / 100,
        revenueImpact / 100
      ];
      
      // Add year volumes
      years.forEach(year => {
        row.push(part.baseline_volumes?.[year] || 0);
        row.push(part.scenario_volumes?.[year] || 0);
      });
      
      excelData.push(row);
    });
    
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Set column widths
    const colWidths = [
      { wch: 15 }, // Part Number
      { wch: 40 }, // Description
      { wch: 12 }, // Prices
      { wch: 12 },
      { wch: 12 },
      { wch: 18 }, // Volumes
      { wch: 18 },
      { wch: 15 },
      { wch: 15 }, // Revenue
      { wch: 15 },
      { wch: 15 }
    ];
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Scenario Data');
    
    // Generate filename
    const filename = `${scenarioData.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download file
    XLSX.writeFile(wb, filename);
  };



  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const value = Math.abs(cents) / 100;
    return cents < 0 
      ? `-$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
      : `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const calculatePartScenarioRevenue = (part) => {
    const partChange = changes[part.part_id] || {};
    const price = partChange.piece_price_cents || part.scenario_piece_price_cents;
    const volumes = partChange.volumes || part.scenario_volumes;
    
    const totalVolume = Object.values(volumes).reduce((sum, vol) => sum + vol, 0);
    return price * totalVolume;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">Failed to load scenario details</div>
      </div>
    );
  }

  const { scenario: scenarioData, parts_comparison } = details;

  // Recalculate totals with current changes
  let totalBaselineRevenue = 0;
  let totalScenarioRevenue = 0;
  
  parts_comparison.forEach(part => {
    totalBaselineRevenue += part.baseline_revenue_cents;
    totalScenarioRevenue += calculatePartScenarioRevenue(part);
  });
  
  const totalDelta = totalScenarioRevenue - totalBaselineRevenue;
  const deltaPercent = totalBaselineRevenue > 0 
    ? ((totalDelta / totalBaselineRevenue) * 100).toFixed(2)
    : 0;

  // Get years for volume columns (from first part that has volumes)
  const volumeYears = parts_comparison.length > 0 
    ? Object.keys(parts_comparison[0].baseline_volumes || {}).sort()
    : [];

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-bold text-gray-900">{scenarioData.name}</h2>
              <Badge 
                className={
                  scenarioData.status === 'active' ? 'bg-green-600' :
                  scenarioData.status === 'partially_active' ? 'bg-amber-600' :
                  scenarioData.status === 'not_active' ? 'bg-red-500' :
                  scenarioData.status === 'draft' ? 'bg-gray-500' :
                  'bg-gray-400'
                }
                title={
                  scenarioData.status === 'partially_active' 
                    ? `Active for ${parts_comparison.filter(p => p.is_active).length} of ${parts_comparison.length} parts - check badges below` 
                    : undefined
                }
              >
                {scenarioData.status === 'partially_active' ? 'Partially Active' : 
                 scenarioData.status === 'not_active' ? 'Inactive' :
                 scenarioData.status.charAt(0).toUpperCase() + scenarioData.status.slice(1)}
              </Badge>
              {scenarioData.status === 'partially_active' && (
                <span className="text-xs text-amber-700">
                  (Active for {parts_comparison.filter(p => p.is_active).length}/{parts_comparison.length} parts)
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600">{scenarioData.description || 'No description'}</p>
            <p className="text-xs text-gray-500 mt-1">{scenarioData.opportunity_name} • {parts_comparison.length} parts</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges || saving}
              size="sm"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
            
            {scenarioData.status === 'draft' && (
              <Button
                onClick={handleActivate}
                disabled={activating || hasUnsavedChanges}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                title="Use this scenario for opportunity planning rollups without overwriting part master forecasts."
              >
                {activating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Activating...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Activate
                  </>
                )}
              </Button>
            )}
            
            <Button
              onClick={handleExportToExcel}
              variant="outline"
              size="sm"
              title="Export scenario data to Excel spreadsheet"
            >
              <Download className="h-4 w-4" />
            </Button>
            
            <Button
              onClick={handleDuplicate}
              variant="outline"
              size="sm"
              title="Duplicate this scenario"
            >
              <Copy className="h-4 w-4" />
            </Button>
            
            <Button
              onClick={() => {
                setShowVersionHistory(!showVersionHistory);
                if (!showVersionHistory) fetchVersions();
              }}
              variant="outline"
              size="sm"
              title="View version history"
            >
              <History className="h-4 w-4" />
            </Button>
            
            <Button
              onClick={handleDelete}
              variant="outline"
              size="sm"
              className="text-red-600 hover:bg-red-50"
              title="Archive this scenario"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {hasUnsavedChanges && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertDescription className="text-amber-800 text-sm">
              You have unsaved changes. Click "Save Changes" to update the scenario.
            </AlertDescription>
          </Alert>
        )}

        {/* Comparison Cards - Compact Version */}
        <div className="grid grid-cols-4 gap-3 mt-4">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Baseline Revenue</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatCurrency(totalBaselineRevenue)}
            </div>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Scenario Revenue</div>
            <div className="text-lg font-semibold text-blue-900">
              {formatCurrency(totalScenarioRevenue)}
            </div>
          </div>
          
          <div className={`border rounded-lg p-3 ${totalDelta >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="text-xs text-gray-500 mb-1">Revenue Impact</div>
            <div className={`text-lg font-semibold ${totalDelta >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(totalDelta)}
            </div>
            <div className={`text-xs ${totalDelta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {deltaPercent}% change
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Parts Count</div>
            <div className="text-lg font-semibold text-gray-900">
              {parts_comparison.length}
            </div>
          </div>
        </div>
      </div>

      {/* Parts Overview & Expand */}
      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white rounded-lg border border-gray-200">
          {/* Header with Expand Button */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Parts Overview</h3>
              <p className="text-sm text-gray-600">{parts_comparison.length} parts in this scenario</p>
            </div>
            <Button
              onClick={() => setShowExpandedTable(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Maximize2 className="h-4 w-4 mr-2" />
              Open Full Editor
            </Button>
          </div>

          {/* Compact Parts Summary */}
          <div className="p-4">
            <div className="grid gap-3">
              {parts_comparison.slice(0, 5).map((part) => {
                const partChange = changes[part.part_id] || {};
                const scenarioRevenue = calculatePartScenarioRevenue(part);
                const revenueDelta = scenarioRevenue - part.baseline_revenue_cents;
                
                // Calculate scenario price and volume
                const scenarioPrice = partChange.piece_price_cents !== undefined 
                  ? partChange.piece_price_cents 
                  : part.scenario_piece_price_cents;
                
                const scenarioVolumes = partChange.volumes || part.scenario_volumes;
                const totalScenarioVolume = Object.values(scenarioVolumes).reduce((a, b) => a + b, 0);
                const totalBaselineVolume = Object.values(part.baseline_volumes).reduce((a, b) => a + b, 0);
                
                return (
                  <div key={part.part_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{part.part_number}</span>
                        {part.is_active && (
                          <span 
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full"
                            title="This scenario is currently active for this part"
                          >
                            <CheckCircle className="h-3 w-3" />
                            Active
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 truncate max-w-64">{part.description}</div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Price</div>
                        <div className="text-xs">
                          <span className="text-gray-600">${(part.baseline_piece_price_cents / 100).toFixed(2)}</span>
                          <span className="mx-1">→</span>
                          <span className="font-medium text-blue-700">${(scenarioPrice / 100).toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Volume</div>
                        <div className="text-xs">
                          <span className="text-gray-600">{totalBaselineVolume.toLocaleString()}</span>
                          <span className="mx-1">→</span>
                          <span className="font-medium text-blue-700">{totalScenarioVolume.toLocaleString()}</span>
                        </div>
                      </div>
                      <div className={`text-center font-semibold ${revenueDelta >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        <div className="text-xs text-gray-500">Impact</div>
                        <div>{formatCurrency(revenueDelta)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {parts_comparison.length > 5 && (
                <div className="text-center py-3">
                  <span className="text-sm text-gray-600">
                    And {parts_comparison.length - 5} more parts...
                  </span>
                </div>
              )}
            </div>

            {parts_comparison.length === 0 && (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No parts in this scenario</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Version History Panel */}
      {showVersionHistory && (
        <div className="mt-4 bg-white border border-gray-300 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <History className="h-4 w-4" />
              Version History (Last 10)
            </h3>
            <Button
              onClick={() => setShowVersionHistory(false)}
              variant="ghost"
              size="sm"
            >
              Close
            </Button>
          </div>
          
          {versions.length === 0 ? (
            <p className="text-sm text-gray-600 text-center py-4">
              No version history yet. Versions are created when you save changes.
            </p>
          ) : (
            <div className="space-y-2">
              {versions.map((version) => (
                <div 
                  key={version.id}
                  className="flex items-start justify-between p-3 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900">
                        v{version.version_number}
                      </span>
                      <span className="text-xs text-gray-600">
                        {new Date(version.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-700 mb-1">
                      {version.change_summary || 'Updated'}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span>{version.part_count} parts</span>
                      <span className={version.revenue_delta_cents >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {formatCurrency(version.revenue_delta_cents)} impact
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-gray-500 text-xs">{version.status}</Badge>
                    <p className="text-xs text-gray-500 mt-1">by {version.created_by}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expanded Table Modal */}
      <ExpandedPartsTable
        open={showExpandedTable}
        onClose={() => setShowExpandedTable(false)}
        parts_comparison={parts_comparison}
        changes={changes}
        onPartChange={handlePartChange}
        onSave={handleSave}
        saving={saving}
        formatCurrency={formatCurrency}
      />
    </div>
  );
}
